import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useLang, LangToggle } from '../i18n';

const TYPE_LABEL = {
  daily_card:      '每日领取房卡',
  daily_points:    '每日领取积分',
  enter_room_card: '入局扣房卡',
  enter_room_pts:  '入局扣携带积分',
  settle_win:      '对局结算回账',
  share_win:       '瓜分奖池',
  settle_bust:     '爆仓清零',
  refund:          '开局失败退款',
  agent_transfer:  '代理转移房卡',
  recharge:        '充值购买',
  card_sale_income:'房卡出售收入',
  card_purchase:   '购买房卡',
  invite_card_claim: '邀请奖励房卡',
  usdt_manual_settlement: 'USDT 人工结算',
  withdraw_usdt:  'USDT 提现',
  withdraw_usdt_refund: 'USDT 提现退回',
  withdraw_usdt_chest: '宝箱 USDT 提现',
  withdraw_usdt_chest_refund: '宝箱 USDT 退回',
};

const ASSET_LABEL = {
  card: '卡',
  points: 'COIN',
  usdt: 'USDT',
};

const ASSET_LABEL_EN = {
  card: 'Card',
  points: 'COIN',
  usdt: 'USDT',
};

// Map backend Chinese note strings → English
const NOTE_EN = {
  '结算回账': 'Settlement',
  '入局扣房卡': 'Room card used',
  '入局扣携带积分': 'Carry deducted',
  '爆仓清零': 'Busted',
  '开局失败退款': 'Refund',
  '瓜分奖池': 'Bust pool share',
  '每日领取': 'Daily claim',
  '充值': 'Recharge',
  '购买房卡': 'Card purchase',
  '房卡出售': 'Card sale',
  '代理转移': 'Card transfer',
  '提现': 'Withdrawal',
};

function displayAmount(row) {
  const raw = row.amount_decimal ?? row.amount;
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (row.amount_decimal !== null && row.amount_decimal !== undefined) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 18 });
  }
  return n.toLocaleString();
}

function numericAmount(row) {
  return Number(row.amount_decimal ?? row.amount);
}

export default function Ledger() {
  const [list, setList]   = useState([]);
  const [page, setPage]   = useState(1);
  const [loading, setL]   = useState(true);
  const nav = useNavigate();
  const { t, lang } = useLang();

  const TYPE_LABEL_EN = {
    daily_card:'Daily Room Card',daily_points:'Daily Points',
    enter_room_card:'Room Card Used',enter_room_pts:'Carry Deducted',
    settle_win:'Round Settled',share_win:'Bust Pool Share',
    settle_bust:'Busted',refund:'Refund',
    agent_transfer:'Card Transfer',recharge:'Purchase',
    card_sale_income:'Card Sale Income',card_purchase:'Buy Card',
    invite_card_claim:'Invite Card Reward',
    usdt_manual_settlement:'USDT Settlement',
    withdraw_usdt:'USDT Withdraw',withdraw_usdt_refund:'USDT Refund',
    withdraw_usdt_chest:'Chest USDT Withdraw',withdraw_usdt_chest_refund:'Chest USDT Refund',
  };

  useEffect(() => {
    setL(true);
    API.get(`/api/game/ledger?page=${page}`)
      .then(r => setList(r.list || []))
      .catch(() => {})
      .finally(() => setL(false));
  }, [page]);

  const S = `
    .ld-root{min-height:100vh;
      background:linear-gradient(180deg,#0B1E4A 0%,#0D2B6B 50%,#091840 100%);
      background-attachment:fixed;}
    .ld-header{display:flex;align-items:center;justify-content:space-between;
      padding:52px 20px 20px;border-bottom:1px solid rgba(46,127,232,0.2);}
    .ld-title{font-size:22px;font-weight:900;color:#fff;}
    .ld-back{padding:8px 16px;border-radius:10px;border:1px solid rgba(46,127,232,0.35);
      background:rgba(46,127,232,0.1);color:#8BA8D4;font-size:13px;font-weight:600;cursor:pointer;}
    .ld-list{padding:16px 20px;display:flex;flex-direction:column;gap:8px;}
    .ld-row{display:flex;align-items:center;justify-content:space-between;
      padding:14px 16px;border-radius:14px;background:#0F2554;
      border:1px solid rgba(46,127,232,0.25);box-shadow:inset 0 1px 0 rgba(255,255,255,0.05);}
    .ld-type{font-size:14px;font-weight:700;color:#fff;margin-bottom:3px;}
    .ld-note{font-size:12px;color:#4A6A9A;}
    .ld-date{font-size:11px;color:#4A6A9A;margin-top:2px;}
    .ld-amount{font-family:'Share Tech Mono',monospace;font-size:18px;font-weight:700;text-align:right;}
    .ld-asset{font-size:11px;color:#4A6A9A;margin-left:3px;}
    .ld-page{display:flex;justify-content:center;align-items:center;gap:12px;
      padding:20px;margin-top:8px;}
    .ld-page-btn{padding:9px 20px;border-radius:10px;border:1px solid rgba(46,127,232,0.35);
      background:rgba(46,127,232,0.12);color:#8BA8D4;font-size:13px;font-weight:700;cursor:pointer;}
    .ld-page-btn:disabled{opacity:0.35;cursor:not-allowed;}
    .ld-empty{text-align:center;color:#4A6A9A;padding:60px 0;font-size:14px;}
  `;

  return (
    <div className="ld-root">
      <style>{S}</style>

      <div className="ld-header">
        <div className="ld-title">{t('ledgerTitle')}</div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <LangToggle />
          <button className="ld-back" onClick={() => nav('/lobby')}>{lang==='en'?'← Back':'← 返回'}</button>
        </div>
      </div>

      <div className="ld-list">
        {loading ? (
          <div className="ld-empty">{t('loading')}</div>
        ) : list.length === 0 ? (
          <div className="ld-empty">{lang==='en'?'No records yet':'暂无记录'}</div>
        ) : (
          <>
            {list.map((l, i) => (
              <div key={i} className="ld-row fade-in">
                <div>
                  <div className="ld-type">{(lang==='en'?TYPE_LABEL_EN:TYPE_LABEL)[l.type] || l.type}</div>
                  {l.note && <div className="ld-note">{lang==='en' ? (NOTE_EN[l.note] || l.note) : l.note}</div>}
                  <div className="ld-date">
                    {new Date(l.created_at).toLocaleString(lang==='en'?'en-US':'zh-CN')}
                    {l.room_id && <span style={{marginLeft:8}}>{lang==='en'?'Room':'房间'}#{l.room_id}</span>}
                  </div>
                </div>
                <div className="ld-amount" style={{
                  color: numericAmount(l) > 0 ? '#2ECC71' : '#E8453C',
                }}>
                  {numericAmount(l) > 0 ? '+' : ''}{displayAmount(l)}
                  <span className="ld-asset">{(lang==='en' ? ASSET_LABEL_EN : ASSET_LABEL)[l.asset] || l.asset}</span>
                </div>
              </div>
            ))}

            <div className="ld-page">
              <button className="ld-page-btn"
                onClick={() => setPage(p => Math.max(1,p-1))}
                disabled={page===1}>{t('prevPage')}</button>
              <span style={{color:'#8BA8D4',fontSize:14}}>{lang==='en'?`Page ${page}`:`第${page}页`}</span>
              <button className="ld-page-btn"
                onClick={() => setPage(p => p+1)}
                disabled={list.length < 20}>{t('nextPage')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
